import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './HelpTipButton.css';

type Props = {
  /** Long help text shown in the popover */
  content: string;
  /** Short label for accessibility */
  ariaLabel: string;
  /** Classes for the trigger button (e.g. admin-card-tip) */
  buttonClassName?: string;
  /** Optional icon; default is ?-in-circle */
  children?: React.ReactNode;
  /** Wider popover + pre-wrap (e.g. API error dumps) */
  wide?: boolean;
};

function DefaultIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/**
 * Click (or Enter/Space) opens a fixed popover with help text.
 * Hover-only `title` tooltips are unreliable on touch and feel broken on click.
 */
export function HelpTipButton({
  content,
  ariaLabel,
  buttonClassName = '',
  children,
  wide = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const popoverId = `help-tip-${id}`;

  const positionPopover = useCallback(() => {
    const btn = btnRef.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const b = btn.getBoundingClientRect();
    const margin = 8;
    const maxW = Math.min(wide ? 560 : 340, window.innerWidth - margin * 2);
    pop.style.maxWidth = `${maxW}px`;
    const w = pop.offsetWidth;
    let left = b.left + b.width / 2 - w / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - w - margin));
    let top = b.bottom + margin;
    const h = pop.offsetHeight;
    if (top + h > window.innerHeight - margin) {
      top = Math.max(margin, b.top - h - margin);
    }
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }, [wide]);

  useLayoutEffect(() => {
    if (!open) return;
    positionPopover();
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => positionPopover());
    });
    const ro = () => positionPopover();
    window.addEventListener('resize', ro);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', ro);
    };
  }, [open, content, positionPopover, wide]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = e.target as Node;
      if (btnRef.current?.contains(el) || popRef.current?.contains(el)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => setOpen((o) => !o);

  const popover =
    open &&
    createPortal(
      <div
        ref={popRef}
        id={popoverId}
        className={`help-tip-popover${wide ? ' help-tip-popover--wide' : ''}`}
        role="tooltip"
        style={{ position: 'fixed', zIndex: 10050 }}
      >
        <p className="help-tip-popover__text">{content}</p>
      </div>,
      document.body
    );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={buttonClassName}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
      >
        {children ?? <DefaultIcon />}
      </button>
      {popover}
    </>
  );
}
